import { tinycldConfig } from '@tinycld/app-generated/tinycld-config'
import type { ComponentType, LazyExoticComponent } from 'react'
import type {
    PackageProviderLoader,
    PackageSettingsPanel,
    PackageSystemSettingsPanel,
    SidebarContribution,
} from './config-types'

interface SidebarProps {
    isCollapsed: boolean
}
type SidebarComp = ComponentType<SidebarProps> | LazyExoticComponent<ComponentType<SidebarProps>>

type ComponentEntryLike = {
    manifest: { slug: string }
    sidebar?: SidebarComp | null
    provider?: PackageProviderLoader | null
}

/** slug → sidebar component (null when the package contributes none). */
export function deriveSidebars(
    entries: readonly ComponentEntryLike[]
): Record<string, SidebarComp | null> {
    const out: Record<string, SidebarComp | null> = {}
    for (const e of entries) out[e.manifest.slug] = e.sidebar ?? null
    return out
}

/** slug → context provider loader (null when the package contributes none). */
export function deriveProviders(
    entries: readonly ComponentEntryLike[]
): Record<string, PackageProviderLoader | null> {
    const out: Record<string, PackageProviderLoader | null> = {}
    for (const e of entries) out[e.manifest.slug] = e.provider ?? null
    return out
}

export interface PackageSettingsGroup {
    packageName: string
    pkgSlug: string
    icon: string | undefined
    panels: PackageSettingsPanel[]
}

type SettingsEntryLike = {
    manifest: { name: string; slug: string; nav?: { icon?: string } }
    settings?: PackageSettingsPanel[]
}

/** Settings panels grouped by package, omitting packages that contribute none. */
export function deriveSettings(entries: readonly SettingsEntryLike[]): PackageSettingsGroup[] {
    const out: PackageSettingsGroup[] = []
    for (const e of entries) {
        if (e.settings && e.settings.length > 0) {
            out.push({
                packageName: e.manifest.name,
                pkgSlug: e.manifest.slug,
                icon: e.manifest.nav?.icon,
                panels: e.settings,
            })
        }
    }
    return out
}

type AccountSettingsEntryLike = {
    manifest: SettingsEntryLike['manifest']
    accountSettings?: PackageSettingsPanel[]
}

/** Per-user settings panels grouped by package, omitting packages that contribute none. */
export function deriveAccountSettings(
    entries: readonly AccountSettingsEntryLike[]
): PackageSettingsGroup[] {
    return deriveSettings(entries.map(e => ({ manifest: e.manifest, settings: e.accountSettings })))
}

export interface PackageSystemSettingsGroup {
    packageName: string
    pkgSlug: string
    icon: string | undefined
    panels: PackageSystemSettingsPanel[]
}

type SystemSettingsEntryLike = {
    manifest: { name: string; slug: string; nav?: { icon?: string } }
    systemSettings?: PackageSystemSettingsPanel[]
}

/**
 * System-wide settings panels grouped by package, omitting packages that
 * contribute none. Mirrors deriveSettings but for the /admin Settings section
 * (system scope, not org scope).
 */
export function deriveSystemSettings(
    entries: readonly SystemSettingsEntryLike[]
): PackageSystemSettingsGroup[] {
    const out: PackageSystemSettingsGroup[] = []
    for (const e of entries) {
        if (e.systemSettings && e.systemSettings.length > 0) {
            out.push({
                packageName: e.manifest.name,
                pkgSlug: e.manifest.slug,
                icon: e.manifest.nav?.icon,
                panels: e.systemSettings,
            })
        }
    }
    return out
}

export interface SidebarContributionEntry {
    contributorSlug: string
    order: number
    label?: string
    Component: ComponentType | LazyExoticComponent<ComponentType>
}

type ContributionEntryLike = {
    manifest: { slug: string }
    sidebarContributions?: SidebarContribution[]
}

/**
 * target slug → slot name → sorted contributions.
 * Stable sort: ascending by `order`, ties broken by contributor slug.
 */
export function deriveSidebarContributions(
    entries: readonly ContributionEntryLike[]
): Record<string, Record<string, SidebarContributionEntry[]>> {
    const out: Record<string, Record<string, SidebarContributionEntry[]>> = {}
    for (const e of entries) {
        const contributions = e.sidebarContributions
        if (!contributions || contributions.length === 0) continue
        for (const c of contributions) {
            let bySlot = out[c.target]
            if (!bySlot) {
                bySlot = {}
                out[c.target] = bySlot
            }
            let list = bySlot[c.slot]
            if (!list) {
                list = []
                bySlot[c.slot] = list
            }
            list.push({
                contributorSlug: e.manifest.slug,
                order: c.order,
                label: c.label,
                Component: c.Component,
            })
        }
    }
    for (const target of Object.keys(out)) {
        for (const slot of Object.keys(out[target])) {
            out[target][slot].sort((a, b) => {
                if (a.order !== b.order) return a.order - b.order
                return a.contributorSlug.localeCompare(b.contributorSlug)
            })
        }
    }
    return out
}

export const packageSidebars: Record<string, SidebarComp | null> = deriveSidebars(tinycldConfig)
export const packageProviders = deriveProviders(tinycldConfig)
export const packageSettings = deriveSettings(tinycldConfig)
export const packageAccountSettings = deriveAccountSettings(tinycldConfig)
export const packageSystemSettings = deriveSystemSettings(tinycldConfig)
export const packageSidebarContributions = deriveSidebarContributions(tinycldConfig)
